import {useEffect,useRef,useState} from 'react'
import {forumEnabled,forumLanguage,forumUnreadRpc} from './forumApi'

export const FORUM_UNREAD_CHANGED = 'hubbing-forum-unread-changed'
const readDelay = 700
const batchLimit = 100

function validSummary(data,language) {
  return data && data.language===language && Number.isSafeInteger(data.count) && data.count>=0 && typeof data.enabled==='boolean'
}

function publishSummary(userId,language,data) {
  if(validSummary(data,language))window.dispatchEvent(new CustomEvent(FORUM_UNREAD_CHANGED,{detail:{userId,language,count:data.count,enabled:data.enabled}}))
}

// This count has its own endpoint. It never creates general-bell events or
// updates notifications belonging to conversations in which a member takes part.
export function useForumUnreadSummary({userId,language='fr',isActive=true}) {
  const lang=forumLanguage(language),[state,setState]=useState(null),version=useRef(0)
  useEffect(()=>{
    if(!forumEnabled()||!userId)return undefined
    const changed=event=>{
      const data=event.detail
      if(data?.userId===userId&&validSummary(data,lang)){version.current++;setState({userId,...data})}
    }
    window.addEventListener(FORUM_UNREAD_CHANGED,changed)
    return()=>window.removeEventListener(FORUM_UNREAD_CHANGED,changed)
  },[userId,lang])
  useEffect(()=>{
    if(!forumEnabled()||!userId||!isActive)return undefined
    const controller=new AbortController()
    let current=true,busy=false
    const refresh=async()=>{
      if(!current||document.hidden||busy)return
      busy=true
      const startedVersion=version.current
      try{
        const data=await forumUnreadRpc('summary',{language:lang},{signal:controller.signal,expectedUserId:userId})
        if(current&&version.current===startedVersion&&validSummary(data,lang))setState({userId,...data})
      }catch{/* Entry navigation remains available when the counter is unavailable. */}
      finally{busy=false}
    }
    refresh()
    window.addEventListener('focus',refresh)
    document.addEventListener('visibilitychange',refresh)
    const timer=window.setInterval(refresh,45000)
    return()=>{current=false;controller.abort();window.clearInterval(timer);window.removeEventListener('focus',refresh);document.removeEventListener('visibilitychange',refresh)}
  },[userId,lang,isActive])
  return state?.userId===userId&&state.language===lang?state:{count:0,enabled:false,language:lang}
}

// A receipt is sent only after an approved post has stayed inside the usable
// reading viewport. Loading a thread or opening the overview marks nothing.
export function useVisibleForumReadReceipts({userId,topicId,language='fr',postIds=[],containerRef,isActive=false}) {
  const lang=forumLanguage(language),scope=`${userId||''}:${topicId||''}`
  const remembered=useRef({scope,ids:new Set()})
  if(remembered.current.scope!==scope)remembered.current={scope,ids:new Set()}
  const postKey=postIds.join(',')
  useEffect(()=>{
    if(!forumEnabled()||!userId||!topicId||!isActive||!containerRef.current||typeof IntersectionObserver==='undefined')return undefined
    const allowed=new Set(postKey.split(',').filter(Boolean)),seen=remembered.current.ids,processed=new Set()
    seen.forEach(id=>{if(!allowed.has(id))seen.delete(id)})
    const elements=[...containerRef.current.querySelectorAll('[data-forum-readable-post]')].filter(element=>allowed.has(element.dataset.forumReadablePost))
    if(!elements.length)return undefined
    const controller=new AbortController(),visible=new Map()
    let current=true,timer,request=false,failedAttempts=0
    const canRead=()=>!document.hidden&&!document.activeElement?.matches?.('input,textarea,[contenteditable="true"]')
    const nav=document.querySelector('nav[aria-label="Navigation principale"]')
    const bottom=Math.ceil(nav?.getBoundingClientRect().height||0)+12
    const schedule=(delay=readDelay)=>{
      if(current&&canRead()&&!timer&&!request&&failedAttempts<2)timer=window.setTimeout(flush,delay)
    }
    const flush=async()=>{
      timer=undefined
      if(!current||!canRead()||request)return
      const now=Date.now()
      const ids=[...visible].filter(([id,entered])=>!seen.has(id)&&!processed.has(id)&&now-entered>=readDelay).map(([id])=>id).slice(0,batchLimit)
      if(!ids.length){if([...visible.keys()].some(id=>!seen.has(id)&&!processed.has(id)))schedule();return}
      request=true
      try{
        const data=await forumUnreadRpc('mark_read',{language:lang,topicId,postIds:ids},{signal:controller.signal,expectedUserId:userId})
        if(!current||remembered.current.scope!==scope)return
        ids.forEach(id=>processed.add(id))
        const confirmed=Array.isArray(data.readPostIds)?data.readPostIds:(data.marked===ids.length?ids:[])
        confirmed.filter(id=>ids.includes(id)).forEach(id=>seen.add(id));failedAttempts=0
        publishSummary(userId,lang,data)
      }catch{
        failedAttempts++
      }finally{
        request=false
        if(current&&failedAttempts<2&&[...visible.keys()].some(id=>!seen.has(id)&&!processed.has(id)))schedule(failedAttempts?10000:readDelay)
      }
    }
    const observer=new IntersectionObserver(entries=>{
      entries.forEach(entry=>{
        const id=entry.target.dataset.forumReadablePost
        if(!allowed.has(id))return
        if(entry.isIntersecting&&entry.intersectionRatio>=.1){if(!visible.has(id))visible.set(id,Date.now())}
        else visible.delete(id)
      })
      schedule()
    },{root:null,rootMargin:`-64px 0px -${bottom}px 0px`,threshold:[0,.1]})
    elements.forEach(element=>observer.observe(element))
    const visibility=()=>{
      window.clearTimeout(timer);timer=undefined
      if(canRead()){visible.forEach((_,id)=>visible.set(id,Date.now()));schedule()}
    }
    const focusOut=()=>Promise.resolve().then(()=>{if(current)visibility()})
    document.addEventListener('visibilitychange',visibility)
    document.addEventListener('focusin',visibility)
    document.addEventListener('focusout',focusOut)
    return()=>{current=false;controller.abort();observer.disconnect();window.clearTimeout(timer);document.removeEventListener('visibilitychange',visibility);document.removeEventListener('focusin',visibility);document.removeEventListener('focusout',focusOut)}
  },[userId,topicId,lang,scope,postKey,containerRef,isActive])
}
